/**
 * Turning a stored phone number into something tappable.
 *
 * A number in the database is whatever the person typed, minus spaces and
 * dashes - `+919876543210` or the bare `9876543210`, which is all the
 * validator behind every phone field accepts. A link needs more than that: a
 * dialler and `wa.me` both want an unambiguous international number, so the
 * country code a bare mobile only implies has to be written out before either
 * will reach the right person.
 */

/**
 * The country code put in front of a number that was stored without one.
 *
 * Every number the app accepts is an Indian mobile, so the code is not a guess
 * - it is the one the number already had implicitly.
 */
const INDIA_DIAL_CODE = '91';

/**
 * `phone` as bare international digits, or null if it cannot be one.
 *
 * Ten digits is a mobile stored without its code, so it gets India's. Anything
 * already longer is taken to carry its own code. Anything too short or too
 * long to be a phone number at all - a mangled import, an extension someone
 * typed into the field - comes back null, so a caller can leave the link off
 * rather than offer one that fails after the tap.
 */
export function internationalPhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  const international = digits.length === 10 ? `${INDIA_DIAL_CODE}${digits}` : digits;
  return international.length >= 11 && international.length <= 15 ? international : null;
}

/**
 * A `tel:` link that dials `phone`, or null if there is no number to dial.
 *
 * Written with the `+` because that is what makes the number international to
 * a dialler: without it a phone reads the digits as local and prepends nothing
 * or the wrong thing.
 */
export function telUrl(phone: string | null | undefined): string | null {
  const digits = internationalPhone(phone);
  return digits === null ? null : `tel:+${digits}`;
}

/**
 * The dialling codes offered beside a WhatsApp number, India first: it is who
 * the product is for, and so the default. The rest are where its merchants'
 * families are.
 */
export const WHATSAPP_COUNTRY_CODES = [
  { value: '91', label: 'IN +91' },
  { value: '1', label: 'US +1' },
  { value: '44', label: 'UK +44' },
  { value: '971', label: 'AE +971' },
  { value: '966', label: 'SA +966' },
  { value: '65', label: 'SG +65' },
  { value: '61', label: 'AU +61' },
  { value: '977', label: 'NP +977' },
  { value: '880', label: 'BD +880' },
  { value: '94', label: 'LK +94' },
] as const;

export const DEFAULT_WHATSAPP_COUNTRY_CODE = INDIA_DIAL_CODE;
